import { useState, useRef, useEffect, useMemo } from 'react';
// deviation: the monolith imported Tablet at module scope but CourtPadCard never
// uses it (only CourtPadTab does), so it is omitted here to satisfy lint.
import { Check, Timer } from 'lucide-react';
import { TOURNAMENT_TYPES, SCORE_MIN, SCORE_MAX, clampScoreInput } from '../../engine';
import { deviceFillBoxStyle } from './deviceFill';
import { MatchupLines, ConfirmResultModal } from '../tournament';
import { useUnsavedMatchScore } from '../../state/unsavedScores';

// ─────────────────────────────────────────────────────────────────────
// TAKING A POINT BACK.
//
// The pad could only count UP. Reported: «hvis man av mistak trykker inn for
// mye poeng bør det finnes en funksjon å redusere». The score fields are typable,
// so the score was never actually stuck — but this pad is an iPad on a wall, and
// «type the right number» there means summoning an on-screen keyboard over the
// match you are scoring. A mis-tap deserves a tap back, not a keyboard.
//
// DELIBERATELY SMALLER AND QUIETER THAN `+`. A point is added dozens of times a
// match and taken back once; two equal buttons either side of a number would
// make the common action a choice. `clampScoreInput` already floors at
// SCORE_MIN, so «−» at zero simply does nothing.
//
// «↺ Nullstill» below the row already clears both scores and stays as it is.
// ─────────────────────────────────────────────────────────────────────
const PAD_MINUS_CLASS =
  'flex-shrink-0 flex items-center justify-center font-mono font-bold transition-all hover:brightness-95 active:scale-95';
const PAD_MINUS_STYLE = {
  width: 40,
  height: 40,
  borderRadius: 10,
  background: '#eef0ea',
  color: '#6b766f',
  fontSize: 26,
  lineHeight: 1,
  border: '1px solid rgba(15, 22, 17, 0.10)',
} as const;

// deviation: STARPOINT_LOGO_WHITE_URI was defined in legacy/App.jsx;
// copied verbatim as a module-local const (public asset path, not logic) so this
// component is self-contained. Mirrors the
// precedent in src/components/ui/Padel24Logo.tsx.
const STARPOINT_LOGO_WHITE_URI = '/starpoint-logo-white.png';

// deviation: readableTextOn is a pure helper that was defined at module scope in
// legacy/App.jsx; copied verbatim as a module-local helper
// (no closure state) so this component is self-contained. Behaviour unchanged.
function readableTextOn(hex) {
  const h = String(hex || '').replace('#', '');
  if (h.length < 6) return '#ffffff';
  const r = parseInt(h.slice(0, 2), 16),
    g = parseInt(h.slice(2, 4), 16),
    b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? '#0a0b10' : '#ffffff';
}

export interface CourtPadCardProps {
  /** Court number/id shown in the pad header. */
  court: any;
  /** Tournament id — namespaces the per-bane localStorage keys (flow/sound/break-skip). */
  tournamentId?: any;
  /** Tournament format key, used to pick the TOURNAMENT_TYPES stripe (default 'padel24'). */
  formatType?: any;
  /** This bane's matches, already sorted chronologically by the parent. */
  matches: any;
  /** Map of matchId → global cross-court match number. */
  globalMatchNumber?: any;
  /** Live `Date` ticked once per second by the parent. */
  now: any;
  /** Minutes since midnight derived from `now`. */
  nowMinutes: any;
  /** Today's date string (YYYY-MM-DD) derived from `now`. */
  todayStr: any;
  /** Map of dayId → match duration in minutes. */
  matchDurations: any;
  /** Map of date (YYYY-MM-DD) → list of break blocks for the feed. */
  breaksByDate?: any;
  /** Tournament default flow mode ('result' | 'time') before per-bane override. */
  defaultFlowMode?: any;
  /** The tournament's players (for MatchupLines name lookup). */
  players?: any;
  /** Callback to record a result (matchId, s1, s2, source). */
  onRecordResult?: any;
  /**
   * What the pad's own submission is doing, and for WHICH match (#216).
   *
   * The button the operator pressed is the only place they are looking in the
   * second after they press it, so it reports on itself rather than leaving
   * the screen silent. Matched by id: a court showing several matches must not
   * grey out the wrong one. Absent (Sentralen's preview) ⇒ nothing changes.
   */
  submitStatus?: { phase: 'idle' | 'sending' | 'done'; matchId: string | null };
  /** Persists the in-progress (live) points without finalising: (matchId, { score1, score2 }). */
  onLiveScore?: any;
  /** Callback when a player chip is clicked. */
  onPlayerClick?: any;
  /** Optional pinned club-score banner element rendered inside the pad. */
  clubBanner?: any;
  /**
   * Plays an end-sound by key — shared audio helper from the monolith (Web Audio).
   * NOTE: this was defined at module scope in legacy/App.jsx; the call-site
   * must pass it. (deviation: the monolith read this via closure.)
   */
  playEndSound: (soundType?: string) => void;
  /**
   * Device kiosk mode: drop the maxWidth/maxHeight caps so the pad fills its
   * (already screen-fitted) container. Sentralen leaves this false so the
   * stacked preview keeps its compact sizing.
   */
  fill?: boolean;
}

// An "iPad card" for a single court
/**
 * Banepad "iPad card" for a single court — fixed header (bane/Starpoint/clock),
 * a scrolling chronological feed of matches and breaks with a centred current/next
 * focus block, per-bane flow-mode + sound toggles, inline score entry and edit, and
 * a confirm modal. Extracted verbatim from the monolith (legacy/App.jsx); markup/logic
 * unchanged. Takes `playEndSound` as a prop (the helper was module-scoped in the
 * monolith).
 */
export function CourtPadCard({
  court,
  tournamentId,
  formatType = 'padel24',
  matches,
  globalMatchNumber,
  now,
  nowMinutes,
  todayStr,
  matchDurations,
  breaksByDate = {},
  defaultFlowMode = 'result',
  players,
  onRecordResult,
  submitStatus,
  onLiveScore = null,
  onPlayerClick,
  clubBanner = null,
  playEndSound,
  fill = false,
}: CourtPadCardProps) {
  // State for the ongoing match's score input
  const [score1, setScore1] = useState('');
  const [score2, setScore2] = useState('');

  // State for editing a previous match
  const [editingId, setEditingId] = useState(null);
  const [editS1, setEditS1] = useState('');
  const [editS2, setEditS2] = useState('');

  // State for the confirmation modal
  const [pendingConfirm, setPendingConfirm] = useState(null);

  // Scroll references
  const scrollContainerRef = useRef(null);
  const currentMatchRef = useRef(null);
  const isFirstScrollRef = useRef(true);

  // ============================================================
  // FLOW MODE per court — overrides the tournament's default.
  // ============================================================
  //   'time'   → switches to the next match only at the scheduled time
  //   'result' → switches immediately when a result is recorded
  // Stored in localStorage per tournament+court so the choice is remembered individually.
  const flowStorageKey = `padel24_courtpad_flow_${tournamentId || 'noid'}_${court}`;
  const [flowMode, setFlowMode] = useState(() => {
    try {
      const saved = localStorage.getItem(flowStorageKey);
      // If overridden for this court, use it. Otherwise the tournament's default.
      if (saved === 'time' || saved === 'result') return saved;
      return defaultFlowMode;
    } catch (_e) {
      return defaultFlowMode;
    }
  });

  // If the tournament's default changes (user adjusts it in settings),
  // and the court does NOT have a per-court override, update flowMode.
  useEffect(() => {
    try {
      if (localStorage.getItem(flowStorageKey) === null) {
        setFlowMode(defaultFlowMode);
      }
    } catch (_e) {
      /* ignore */
    }
  }, [defaultFlowMode, flowStorageKey]);

  // deviation: the monolith named this `toggleFlowMode` but never called it (dead code
  // tolerated under @ts-nocheck); renamed `_toggleFlowMode` to satisfy lint
  // (@typescript-eslint/no-unused-vars). Kept verbatim otherwise.
  const _toggleFlowMode = () => {
    const newMode = flowMode === 'time' ? 'result' : 'time';
    setFlowMode(newMode);
    try {
      localStorage.setItem(flowStorageKey, newMode);
    } catch (_e) {
      /* ignore */
    }
  };

  // ============================================================
  // BREAKS — skip-state per break ID
  // ============================================================
  // When flowMode === 'result', a break can be shown large even BEFORE it has
  // actually started (as soon as all preceding matches are done). Admin can then
  // "skip" the break to jump straight to the next match — but the timer keeps
  // ticking in the background, and the sound alert will still play when the break ends.
  const breakSkipStorageKey = `padel24_courtpad_breakskip_${tournamentId || 'noid'}_${court}`;
  const [skippedBreaks, setSkippedBreaks] = useState(() => {
    try {
      const saved = localStorage.getItem(breakSkipStorageKey);
      return saved ? JSON.parse(saved) : {};
    } catch (_e) {
      return {};
    }
  });

  const skipBreak = (breakId) => {
    const next = { ...skippedBreaks, [breakId]: true };
    setSkippedBreaks(next);
    try {
      localStorage.setItem(breakSkipStorageKey, JSON.stringify(next));
    } catch (_e) {
      /* ignore */
    }
  };

  // ============================================================
  // ALERT: whistles a match into play and ends it with a whistle
  // ============================================================
  // Each court has an individual sound state remembered per screen via
  // localStorage. The default for new courts is ON — so the club doesn't have to
  // remember to turn the sound on for each iPad at startup. The user has full control:
  // if you turn it off, that is remembered. If you turn it on again, that is remembered too.
  const storageKey = `padel24_courtpad_sound_${court}`;
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      // If never set before (=== null) → default ON
      // If previously set → respect the user's choice (true/false)
      if (saved === null) return true;
      return saved === 'true';
    } catch (_e) {
      return true;
    }
  });

  // Remember which match events we've already triggered so we don't whistle twice
  // on re-render. Match ID is the key, the value is what has been done:
  // 'started' (whistled into play) and/or 'ended' (whistled off).
  const triggeredEventsRef = useRef(new Map());

  // Ticks every second to check whether a match should be whistled into play or off,
  // or whether a break has ended. flowMode controls what is actually alerted:
  //   - 'time'   → whistle at match start and match end (the same times as
  //                match switches happen in the banepad feed)
  //   - 'result' → do NOT whistle at match times, since the court rolls based on
  //                result entry. The only alert is break-end — that is
  //                the only moment where the clock actually controls anything.
  // Uses the now prop, which is already updated by the parent component, so we
  // don't need our own interval here — useEffect runs on every now change.
  useEffect(() => {
    if (!soundEnabled) return;

    // MATCH EVENTS: only when flowMode === 'time'
    if (flowMode === 'time' && matches && matches.length > 0) {
      matches.forEach((m) => {
        if (m.completed) return;
        if (!m.timeMinutes && m.timeMinutes !== 0) return;
        if (m.date && m.date !== todayStr) return;

        const matchStart = m.timeMinutes;
        const duration = matchDurations?.[m.id] || 27;
        const matchEnd = matchStart + duration;

        const events = triggeredEventsRef.current.get(m.id) || new Set();

        // WHISTLE INTO PLAY at match start
        const minutesIntoMatch = nowMinutes - matchStart;
        if (!events.has('started') && minutesIntoMatch >= 0 && minutesIntoMatch < 0.5) {
          playEndSound('whistle');
          events.add('started');
          triggeredEventsRef.current.set(m.id, events);
        }

        // WHISTLE OFF at match end
        const minutesPastEnd = nowMinutes - matchEnd;
        if (!events.has('ended') && minutesPastEnd >= 0 && minutesPastEnd < 0.5) {
          playEndSound('whistle');
          events.add('ended');
          triggeredEventsRef.current.set(m.id, events);
        }
      });
    }

    // BREAK END: plays regardless of flowMode (the only alert in result mode).
    // Triggered when nowMinutes passes the break's endMin — signals that
    // the courts should be ready for the next match.
    const todaysBreaks = breaksByDate[todayStr] || [];
    todaysBreaks.forEach((b) => {
      const eventKey = `break_${b.id}`;
      const events = triggeredEventsRef.current.get(eventKey) || new Set();
      const minutesPastBreakEnd = nowMinutes - b.endMin;
      if (!events.has('breakEnded') && minutesPastBreakEnd >= 0 && minutesPastBreakEnd < 0.5) {
        playEndSound('whistle');
        events.add('breakEnded');
        triggeredEventsRef.current.set(eventKey, events);
      }
    });
    // DEVIATION (lint): playEndSound is a stable call-site helper read at fire-time;
    // the monolith keeps these exact deps (playEndSound was a module-scoped fn, never
    // a dep). Adding it would not change behaviour but diverges from the verbatim deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowMinutes, soundEnabled, matches, todayStr, matchDurations, flowMode, breaksByDate]);

  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    try {
      localStorage.setItem(storageKey, String(newValue));
    } catch (_e) {
      /* ignore */
    }
    // When the user turns sound on: play a short whistle immediately. This gives
    // 1) confirmation that the sound works, and
    // 2) "unlocks" the browser's audio context (autoplay policy requires a
    //    user gesture before sound can be played automatically).
    if (newValue) {
      playEndSound('whistle');
    }
  };

  // Which break should be shown LARGE (get "current" focus)?
  //   - flowMode === 'time': only breaks that are active NOW (within the time window)
  //   - flowMode === 'result': breaks that aren't skipped AND where all
  //     matches scheduled before the break are done (regardless of whether the clock
  //     has reached the break yet — as soon as the court is "ready" for the break, it shows)
  const bigBreakId = useMemo(() => {
    const todaysBreaks = breaksByDate[todayStr] || [];

    for (const br of todaysBreaks) {
      // Past breaks are never shown large — they display compactly in the feed
      if (nowMinutes >= br.endMin) continue;

      // Time mode: requires that we are within the break window right now
      if (flowMode === 'time') {
        if (nowMinutes >= br.startMin && nowMinutes < br.endMin) return br.id;
        continue;
      }

      // Result mode: skipped breaks are not shown large
      if (skippedBreaks[br.id]) continue;

      // Check whether all matches BEFORE the break are done (or the break is active)
      const hasUnfinishedBefore = matches.some(
        (m) =>
          m.date === todayStr &&
          m.timeMinutes != null &&
          m.timeMinutes < br.startMin &&
          !m.completed,
      );
      if (hasUnfinishedBefore) continue;

      return br.id;
    }
    return null;
  }, [breaksByDate, todayStr, nowMinutes, matches, flowMode, skippedBreaks]);

  // Find the ongoing match.
  //   - flowMode === 'result': the first non-finished match becomes ongoing immediately
  //     EXCEPT if there is a break taking focus (bigBreakId)
  //   - flowMode === 'time': a match becomes ongoing only once its start time has passed
  // Tolerance: -1 min for time mode so matches starting in <60 sec show as ongoing.
  const current = useMemo(() => {
    const notCompleted = matches.filter((m) => !m.completed);
    if (notCompleted.length === 0) return null;

    if (flowMode === 'result') {
      // A break takes focus instead of the next match
      if (bigBreakId) return null;
      return notCompleted[0];
    }

    // Time mode: ongoing only if the start time has passed (or is near)
    const startedOrNear = notCompleted.find((m) => {
      if (m.timeMinutes == null) return true;
      if (m.date && m.date !== todayStr) return false;
      return nowMinutes >= m.timeMinutes - 1;
    });

    return startedOrNear || null;
  }, [matches, nowMinutes, todayStr, flowMode, bigBreakId]);

  // If no match is ongoing right now, find the next scheduled match
  // (the one with the earliest start time among non-finished ones). Used for "Next match" focus.
  // If there is a break taking focus, upcoming is not shown either.
  const upcoming = useMemo(() => {
    if (current) return null;
    if (bigBreakId) return null;
    return matches.find((m) => !m.completed) || null;
  }, [matches, current, bigBreakId]);
  const currentId = current?.id;

  /**
   * A REFRESH FROM A BANEPAD MUST NOT DISCARD THIS (#556).
   *
   * The portal adopts a pad's result silently when it touches no match anybody
   * is typing into. That decision is only safe if every place holding unsent
   * input says so — a surface that forgets loses the operator's work quietly,
   * which is the whole failure this register exists to prevent.
   *
   * Registered against the match ON COURT, which is the one `score1`/`score2`
   * belong to. «Dirty» is having typed something: these two start empty and are
   * only filled by the operator, so anything in them is unsent work.
   */
  useUnsavedMatchScore(currentId, score1 !== '' || score2 !== '');

  /**
   * THE AUTHORITATIVE RESULT WON, SO THE DRAFT GOES (#559 review).
   *
   * Unlike the table rows these start empty rather than seeded, but the same
   * thing goes wrong: after the operator presses «Hent inn resultatet» the
   * tournament carries the pad's score and this card would still show what they
   * chose to discard.
   *
   * Keyed on the match ON COURT and its stored result. A conflicting adoption is
   * withheld until accepted, and an unrelated one does not change these props —
   * so this cannot clear what somebody is mid-way through typing.
   */
  useEffect(() => {
    setScore1('');
    setScore2('');
  }, [currentId, current?.score1, current?.score2, current?.completed]);
  const upcomingId = upcoming?.id;
  // A shared "focus ID" — regardless of whether we are in ongoing or waiting mode,
  // the scroll target is the ID that currentMatchRef points at.
  const focusId = currentId || upcomingId;

  // Reset the score input when the ongoing match changes — resume from a saved
  // in-progress score (liveScore, persisted continuously without finalising) so
  // the points survive a reload mid-match.
  useEffect(() => {
    const ls = current?.liveScore;
    setScore1(ls?.score1 != null ? String(ls.score1) : '');
    setScore2(ls?.score2 != null ? String(ls.score2) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  // Persist the in-progress points continuously WITHOUT finalising the match.
  const livePersist = (s1: any, s2: any) => {
    if (onLiveScore && current && !current.completed) {
      onLiveScore(current.id, {
        score1: s1 === '' || s1 == null ? null : Number(s1),
        score2: s2 === '' || s2 == null ? null : Number(s2),
      });
    }
  };

  // Scroll the focus block to the centre of the view. Triggers on any change
  // of either current or upcoming, so the scroll updates when the court goes
  // from ongoing → waiting → new ongoing.
  useEffect(() => {
    const container = scrollContainerRef.current;
    const target = currentMatchRef.current;
    if (!container || !target) return undefined;
    // Wait for layout (rAF) before measuring — especially important on resume/load
    // where the feed is built at the same time as the effect runs.
    const doScroll = () => {
      const c = scrollContainerRef.current;
      const tEl = currentMatchRef.current;
      if (!c || !tEl) return;
      // getBoundingClientRect is reliable regardless of offsetParent
      // (target.offsetTop was relative to the pad root, not the scroll container).
      const cRect = c.getBoundingClientRect();
      const tRect = tEl.getBoundingClientRect();
      const relativeTop = tRect.top - cRect.top + c.scrollTop;
      const topOffset = 56; // room for the "↑ tidligere kamper" header
      let scrollTop;
      if (tRect.height >= cRect.height - topOffset) {
        // The block is taller than the view → centre it so the whole match is visible
        scrollTop = relativeTop - cRect.height / 2 + tRect.height / 2;
      } else {
        // Pin the ongoing/next match NEAR THE TOP ("second from top")
        scrollTop = relativeTop - topOffset;
      }
      c.scrollTo({
        top: Math.max(0, scrollTop),
        behavior: isFirstScrollRef.current ? 'auto' : 'smooth',
      });
      isFirstScrollRef.current = false;
    };
    const raf = requestAnimationFrame(doScroll);
    return () => cancelAnimationFrame(raf);
  }, [focusId, matches.length]);

  // Submit for the ongoing match (with confirmation)
  const requestSubmit = (match, a, b) => {
    const sa = a === '' || a == null ? 0 : parseInt(a, 10);
    const sb = b === '' || b == null ? 0 : parseInt(b, 10);
    if (isNaN(sa) || isNaN(sb) || sa < 0 || sb < 0) return;
    if (sa === 0 && sb === 0) return;
    setPendingConfirm({ matchId: match.id, s1: sa, s2: sb, match });
  };

  const confirmSubmit = () => {
    if (pendingConfirm) {
      onRecordResult?.(pendingConfirm.matchId, pendingConfirm.s1, pendingConfirm.s2, 'banepad');
    }
    setPendingConfirm(null);
  };

  // Submit for a previous match (without confirmation — direct)
  const saveEdit = (matchId) => {
    const sa = editS1 === '' ? 0 : parseInt(editS1, 10);
    const sb = editS2 === '' ? 0 : parseInt(editS2, 10);
    if (isNaN(sa) || isNaN(sb) || sa < 0 || sb < 0) return;
    if (sa === 0 && sb === 0) return;
    onRecordResult?.(matchId, sa, sb, 'banepad-edit');
    setEditingId(null);
    setEditS1('');
    setEditS2('');
  };

  const startEdit = (match) => {
    setEditingId(match.id);
    setEditS1(String(match.score1 ?? ''));
    setEditS2(String(match.score2 ?? ''));
  };

  // Countdown to the ongoing match
  // Build a combined "feed" — matches + breaks sorted chronologically.
  // Breaks are merged in so they appear at the right spot in the court's timeline.
  // Each entry has { kind: 'match' | 'break', timeMin, data }.
  const feed = useMemo(() => {
    const entries = [];

    matches.forEach((m) => {
      entries.push({
        kind: 'match',
        timeMin: m.timeMinutes != null ? m.timeMinutes : 9999, // unknown time goes last
        date: m.date,
        data: m,
      });
    });

    // Merge in breaks. A break applies to all courts — shown on each.
    Object.entries(breaksByDate).forEach(([date, breaks]) => {
      // deviation: type-only cast (monolith was @ts-nocheck) — Object.entries infers value as unknown
      (breaks as any[]).forEach((b) => {
        entries.push({
          kind: 'break',
          timeMin: b.startMin,
          date,
          data: b,
        });
      });
    });

    // Sort: first by date, then by time
    entries.sort((a, b) => {
      if (a.date !== b.date) return (a.date || '').localeCompare(b.date || '');
      return a.timeMin - b.timeMin;
    });

    return entries;
  }, [matches, breaksByDate]);

  // Is a break active right now? Used if no ongoing match exists —
  // the break becomes the new "focus" block at the centre of the feed.
  // deviation: the monolith named this `activeBreak` but never read it (dead code
  // tolerated under @ts-nocheck); renamed `_activeBreak` to satisfy lint.
  const _activeBreak = useMemo(() => {
    const todaysBreaks = breaksByDate[todayStr] || [];
    return todaysBreaks.find((b) => nowMinutes >= b.startMin && nowMinutes < b.endMin) || null;
  }, [breaksByDate, todayStr, nowMinutes]);

  const countdown = useMemo(() => {
    if (!current || current.timeMinutes == null) return null;
    if (current.date !== todayStr) {
      return { type: 'date', label: current.date };
    }
    const diff = current.timeMinutes - nowMinutes;
    if (diff <= 0) {
      const dur = matchDurations[current.dayId] || 30;
      const remaining = current.timeMinutes + dur - nowMinutes;
      if (remaining > 0) return { type: 'active', minutes: remaining };
      return { type: 'overdue' };
    }
    if (diff < 60) return { type: 'minutes', minutes: diff };
    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;
    return { type: 'hours', hours, minutes };
  }, [current, nowMinutes, todayStr, matchDurations]);

  return (
    <div
      className="rounded-2xl overflow-hidden relative flex flex-col"
      style={{
        background: 'linear-gradient(135deg, #1d2823 0%, #131c18 50%, #0f1611 100%)',
        boxShadow:
          '0 12px 40px -12px rgba(15, 22, 17, 0.6), 0 0 0 1px rgba(255,255,255,0.05) inset',
        border: '7px solid #232f29',
        aspectRatio: '4 / 3',
        width: '100%',
        maxWidth: 900,
        maxHeight: '85vh',
        // Device kiosk: fill the whole tablet (no 4:3 letterbox bars). Overrides
        // the caps/aspect/border above. Empty (no-op) for Sentralen's preview.
        ...deviceFillBoxStyle(fill),
      }}
    >
      {/* Fixed header: BANE · Starpoint · clock */}
      <div
        className="grid items-center px-5 py-3 flex-shrink-0"
        style={{
          background: 'rgba(0, 0, 0, 0.32)',
          borderBottom: '1px solid rgba(194, 240, 47, 0.12)',
          gridTemplateColumns: '1fr auto 1fr',
        }}
      >
        <div className="flex items-baseline gap-2">
          <span
            className="font-mono uppercase tracking-[0.18em]"
            style={{ color: '#94a098', fontSize: 12 }}
          >
            Bane
          </span>
          <span
            className="font-display font-bold leading-none"
            style={{ color: '#c2f02f', fontSize: 28 }}
          >
            {court}
          </span>
        </div>
        <div className="flex items-center justify-center">
          <img
            src={STARPOINT_LOGO_WHITE_URI}
            alt="Starpoint"
            style={{ height: 26, width: 'auto', objectFit: 'contain' }}
          />
        </div>
        <div className="flex items-center justify-end gap-2">
          {/* Flow mode: dropdown with a "Roter etter" label.
              The user picks Tid or Resultat directly without guessing what
              the button will do on click. */}
          <div
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <span
              className="font-mono uppercase tracking-wider"
              style={{ fontSize: 9, color: '#94a098' }}
            >
              Roter etter:
            </span>
            <select
              value={flowMode}
              onChange={(e) => {
                const newMode = e.target.value;
                setFlowMode(newMode);
                try {
                  localStorage.setItem(flowStorageKey, newMode);
                } catch (_err) {
                  /* ignore */
                }
              }}
              className="font-mono uppercase tracking-wider bg-transparent border-0 cursor-pointer"
              style={{
                fontSize: 10,
                color: '#d4dad6',
                appearance: 'none',
                paddingRight: 14,
                backgroundImage:
                  'url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%278%27 height=%275%27 viewBox=%270 0 8 5%27 fill=%27%23d4dad6%27%3E%3Cpath d=%27M0 0l4 5 4-5z%27/%3E%3C/svg%3E")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0 center',
              }}
              title="Velg hva som styrer rotasjon til neste kamp på denne banen"
            >
              <option value="result" style={{ background: '#1d2823', color: '#d4dad6' }}>
                Resultat
              </option>
              <option value="time" style={{ background: '#1d2823', color: '#d4dad6' }}>
                Tid
              </option>
            </select>
          </div>
          <button
            onClick={toggleSound}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full transition-all"
            style={{
              background: soundEnabled ? 'rgba(194, 240, 47, 0.18)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${soundEnabled ? 'rgba(194, 240, 47, 0.4)' : 'rgba(255,255,255,0.08)'}`,
              color: soundEnabled ? '#c2f02f' : '#94a098',
            }}
            title={
              soundEnabled
                ? 'Varsel er på — fløyte blåses ved kamp-start og slutt. Klikk for å slå av.'
                : 'Varsel er av — klikk for å aktivere fløyte ved kamp-start og slutt'
            }
          >
            {soundEnabled ? (
              <>
                {/* Speaker-on icon (SVG) */}
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                </svg>
                <span className="font-mono uppercase tracking-wider" style={{ fontSize: 9 }}>
                  Varsel på
                </span>
              </>
            ) : (
              <>
                {/* Speaker-off icon */}
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <line x1="23" y1="9" x2="17" y2="15" />
                  <line x1="17" y1="9" x2="23" y2="15" />
                </svg>
                <span className="font-mono uppercase tracking-wider" style={{ fontSize: 9 }}>
                  Varsel av
                </span>
              </>
            )}
          </button>
          <span
            className="font-mono font-bold tracking-wider"
            style={{ color: '#d4dad6', fontSize: 20, lineHeight: 1 }}
          >
            {now.toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      </div>

      {/* Tournament-type stripe — between the Starpoint logo and the matches */}
      {(() => {
        const tt = TOURNAMENT_TYPES[formatType] || TOURNAMENT_TYPES.padel24;
        return (
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{ background: tt.color, padding: '6px 16px' }}
          >
            <span
              className="font-mono font-bold uppercase"
              style={{ fontSize: 12, letterSpacing: '0.28em', color: readableTextOn(tt.color) }}
            >
              {tt.label}
            </span>
          </div>
        );
      })()}

      {/* Club score INSIDE the pad (club format only) — pinned, doesn't scroll away */}
      {clubBanner}

      {/* Scrollable feed — all matches in chronological order */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto scrollbar-thin"
        style={{ scrollBehavior: 'smooth' }}
      >
        {matches.length === 0 ? (
          <div
            className="h-full flex items-center justify-center p-8 text-center"
            style={{ color: '#94a098' }}
          >
            <span className="font-mono text-sm uppercase tracking-wider">
              Ingen kamper på denne banen
            </span>
          </div>
        ) : (
          <div className="flex flex-col">
            {/* Toppmessage: scroll up for older */}
            <div className="px-5 py-2 text-center" style={{ color: '#4a544c' }}>
              <span className="font-mono text-[10px] uppercase tracking-wider">
                ↑ tidligere kamper
              </span>
            </div>

            {feed.map((entry, idx) => {
              // BREAK ENTRY — shown as its own block in the feed, the same size as a match
              if (entry.kind === 'break') {
                const b = entry.data;
                const isPastBreak =
                  entry.date < todayStr || (entry.date === todayStr && nowMinutes >= b.endMin);
                const isCurrentlyActive =
                  entry.date === todayStr && nowMinutes >= b.startMin && nowMinutes < b.endMin;
                // LARGE display happens if this break is selected as bigBreakId
                // (can include a "future" break when flowMode === 'result')
                const isBigBreak = b.id === bigBreakId;

                // Compute the countdown — shows "X MIN IGJEN" if active, otherwise
                // "STARTER OM X MIN" if it's in the future on the same day.
                const remainingMin = isCurrentlyActive
                  ? b.endMin - nowMinutes
                  : entry.date === todayStr && nowMinutes < b.startMin
                    ? b.startMin - nowMinutes
                    : 0;

                // Large break display (active NOW, or flow=result and at the front of the queue)
                if (isBigBreak) {
                  return (
                    <div
                      key={`break_${b.id}_${idx}`}
                      ref={!current ? currentMatchRef : null}
                      className="relative"
                      style={{
                        background: 'linear-gradient(180deg, #fffbeb 0%, #fef3c7 100%)',
                        color: '#451a03',
                        borderTop: '3px solid #f59e0b',
                        borderBottom: '3px solid #f59e0b',
                      }}
                    >
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="relative flex h-3 w-3">
                              <span
                                className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                                style={{ background: '#f59e0b' }}
                              ></span>
                              <span
                                className="relative inline-flex rounded-full h-3 w-3"
                                style={{ background: '#f59e0b' }}
                              ></span>
                            </span>
                            <span
                              className="font-mono text-xs uppercase tracking-wider font-bold"
                              style={{ color: '#92400e' }}
                            >
                              {isCurrentlyActive ? 'PAUSE' : 'PAUSE KOMMER'}
                            </span>
                          </div>
                          <span
                            className="font-mono text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded"
                            style={{ background: '#fde68a', color: '#92400e' }}
                          >
                            {isCurrentlyActive
                              ? remainingMin > 0
                                ? `${remainingMin} MIN IGJEN`
                                : 'AVSLUTTES NÅ'
                              : `STARTER OM ${remainingMin} MIN`}
                          </span>
                        </div>
                        <div className="text-center py-6">
                          <div
                            className="font-display font-bold tracking-tight mb-3"
                            style={{ color: '#451a03', fontSize: 64, lineHeight: 1 }}
                          >
                            ☕ {b.label}
                          </div>
                          <div
                            className="font-mono text-base uppercase tracking-wider"
                            style={{ color: '#78350f' }}
                          >
                            {String(Math.floor(b.startMin / 60)).padStart(2, '0')}:
                            {String(b.startMin % 60).padStart(2, '0')}
                            {' – '}
                            {String(Math.floor(b.endMin / 60)).padStart(2, '0')}:
                            {String(b.endMin % 60).padStart(2, '0')}
                            {' · '}
                            {b.durationMin} min
                          </div>
                        </div>

                        {/* Skip button — only when flowMode === 'result'.
                            Closes the break focus so the next match can become current.
                            The break's timer keeps ticking in the background, and
                            the alert at break-end will still play. */}
                        {flowMode === 'result' && (
                          <div
                            className="flex justify-center pt-2 pb-2 border-t"
                            style={{ borderColor: 'rgba(146, 64, 14, 0.18)' }}
                          >
                            <button
                              onClick={() => skipBreak(b.id)}
                              className="font-mono text-xs uppercase tracking-wider font-bold px-4 py-2 rounded-full transition-colors"
                              style={{
                                background: '#fde68a',
                                color: '#92400e',
                                border: '1px solid rgba(146, 64, 14, 0.3)',
                              }}
                              title="Lukk pause-visningen og gå rett til neste kamp. Pause-timeren fortsetter i bakgrunnen og lyd-varsel spiller når pausen er over."
                            >
                              Avslutt pausevisning →
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }

                // Past / upcoming break — SAME STRUCTURE as a compact
                // match row, so the break takes up the same space as a match in the feed.
                // Only colour and opacity change to signal status.
                const bgColor = isPastBreak
                  ? 'rgba(40, 30, 15, 0.4)' // dark, muted for past
                  : 'rgba(245, 158, 11, 0.1)'; // faint yellow for upcoming
                const accentColor = isPastBreak ? '#5a4a30' : '#fbbf24';
                const labelOpacity = isPastBreak ? 0.55 : 0.9;

                return (
                  <div
                    key={`break_${b.id}_${idx}`}
                    className="px-5 py-4 border-b"
                    style={{
                      background: bgColor,
                      borderBottomColor: 'rgba(255,255,255,0.05)',
                      opacity: labelOpacity,
                    }}
                  >
                    {/* Top row: status pill + time */}
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className="font-mono text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded"
                        style={{
                          background: isPastBreak
                            ? 'rgba(90, 74, 48, 0.25)'
                            : 'rgba(245, 158, 11, 0.18)',
                          color: accentColor,
                        }}
                      >
                        {isPastBreak ? '✓ FERDIG' : 'PAUSE'}
                      </span>
                      <span
                        className="font-mono text-[11px] uppercase tracking-wider"
                        style={{ color: accentColor }}
                      >
                        {String(Math.floor(b.startMin / 60)).padStart(2, '0')}:
                        {String(b.startMin % 60).padStart(2, '0')}
                        {' – '}
                        {String(Math.floor(b.endMin / 60)).padStart(2, '0')}:
                        {String(b.endMin % 60).padStart(2, '0')}
                      </span>
                    </div>

                    {/* Main content: break name + icon, centred. Same height
                        as a match row with two team lines. */}
                    <div className="flex items-center justify-center gap-3 py-2">
                      <span style={{ fontSize: 28 }}>☕</span>
                      <div>
                        <div
                          className="font-display font-bold leading-tight"
                          style={{ color: accentColor, fontSize: 22 }}
                        >
                          {b.label}
                        </div>
                        <div
                          className="font-mono text-[10px] uppercase tracking-wider mt-0.5"
                          style={{ color: accentColor, opacity: 0.7 }}
                        >
                          {b.durationMin} minutters pause
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              // MATCH ENTRY
              const m = entry.data;
              const matchNumber = globalMatchNumber?.[m.id] || idx + 1;

              // WAITING FOR THE NEXT MATCH — all matches on the court are either done
              // or waiting for their start time. Shown as a muted "next match" block.
              if (!current && m === upcoming) {
                const minutesUntil =
                  m.timeMinutes != null && m.date === todayStr ? m.timeMinutes - nowMinutes : null;
                const startTimeStr =
                  m.timeMinutes != null
                    ? `${String(Math.floor(m.timeMinutes / 60)).padStart(2, '0')}:${String(m.timeMinutes % 60).padStart(2, '0')}`
                    : '–';

                return (
                  <div
                    key={m.id}
                    ref={currentMatchRef}
                    className="relative"
                    style={{
                      background: 'linear-gradient(180deg, #1a2620 0%, #131c18 100%)',
                      color: '#d4dad6',
                      borderTop: '3px solid rgba(194, 240, 47, 0.3)',
                      borderBottom: '3px solid rgba(194, 240, 47, 0.3)',
                    }}
                  >
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="relative flex h-3 w-3">
                            <span
                              className="relative inline-flex rounded-full h-3 w-3"
                              style={{ background: '#94a098' }}
                            ></span>
                          </span>
                          <span
                            className="font-mono text-xs uppercase tracking-wider font-bold"
                            style={{ color: '#c2f02f' }}
                          >
                            NESTE KAMP
                          </span>
                          <span
                            className="font-mono text-xs uppercase tracking-wider"
                            style={{ color: '#94a098' }}
                          >
                            Kamp {matchNumber} ·{' '}
                            {m.phase === 'finals'
                              ? 'Finale'
                              : m.phase != null
                                ? `Fase ${m.phase}${m.round ? ` · R${m.round}` : ''}`
                                : m.round
                                  ? `Runde ${m.round}`
                                  : ''}
                          </span>
                        </div>
                        <span
                          className="font-mono text-xs font-bold px-2.5 py-0.5 rounded-full"
                          style={{ background: 'rgba(194, 240, 47, 0.15)', color: '#c2f02f' }}
                        >
                          {minutesUntil != null && minutesUntil > 0
                            ? minutesUntil < 60
                              ? `OM ${minutesUntil} MIN`
                              : `OM ${Math.floor(minutesUntil / 60)}T ${minutesUntil % 60}M`
                            : m.date && m.date !== todayStr
                              ? m.date
                              : 'KLAR'}
                        </span>
                      </div>

                      <div className="text-center py-2 mb-3">
                        <div
                          className="font-display font-bold"
                          style={{ color: '#c2f02f', fontSize: 36, lineHeight: 1 }}
                        >
                          {startTimeStr}
                        </div>
                        <div
                          className="font-mono text-[10px] uppercase tracking-wider mt-1"
                          style={{ color: '#94a098' }}
                        >
                          Kamp-start
                        </div>
                      </div>

                      <MatchupLines
                        team1={m.team1}
                        team2={m.team2}
                        players={players}
                        onPlayerClick={onPlayerClick}
                        size="sm"
                      />
                    </div>
                  </div>
                );
              }

              // ONGOING MATCH — large, light, scroll target
              if (m === current) {
                return (
                  <div
                    key={m.id}
                    ref={currentMatchRef}
                    className="relative"
                    style={{
                      background: 'linear-gradient(180deg, #ffffff 0%, #f7f9f1 100%)',
                      color: '#0f1611',
                      borderTop: '3px solid #c2f02f',
                      borderBottom: '3px solid #c2f02f',
                    }}
                  >
                    <div className="p-4">
                      {/* Status row */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="relative flex h-3 w-3">
                            <span
                              className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                              style={{ background: '#84cc16' }}
                            ></span>
                            <span
                              className="relative inline-flex rounded-full h-3 w-3"
                              style={{ background: '#84cc16' }}
                            ></span>
                          </span>
                          <span
                            className="font-mono text-xs uppercase tracking-wider font-bold"
                            style={{ color: '#3f6212' }}
                          >
                            PÅGÅR
                          </span>
                          <span
                            className="font-mono text-xs uppercase tracking-wider"
                            style={{ color: '#3a4a40' }}
                          >
                            Kamp {matchNumber} ·{' '}
                            {m.phase === 'finals'
                              ? 'Finale'
                              : m.phase != null
                                ? `Fase ${m.phase}${m.round ? ` · R${m.round}` : ''}`
                                : m.round
                                  ? `Runde ${m.round}`
                                  : ''}
                          </span>
                          {m.time && (
                            <span className="font-mono text-xs" style={{ color: '#6b766f' }}>
                              · {m.time}
                            </span>
                          )}
                        </div>
                        {countdown && (
                          <span
                            className="font-mono text-xs font-bold px-2.5 py-0.5 rounded-full"
                            style={{
                              background: countdown.type === 'overdue' ? '#fee2e2' : '#eef0ea',
                              color: countdown.type === 'overdue' ? '#b91c1c' : '#3a4a40',
                            }}
                          >
                            {countdown.type === 'overdue' && 'OVERTID'}
                            {countdown.type === 'active' && `${countdown.minutes} MIN IGJEN`}
                            {countdown.type === 'minutes' && `OM ${countdown.minutes} MIN`}
                            {countdown.type === 'hours' &&
                              `OM ${countdown.hours}T ${countdown.minutes}M`}
                            {countdown.type === 'date' && countdown.label}
                          </span>
                        )}
                      </div>

                      {/* Player lines */}
                      <MatchupLines
                        team1={m.team1}
                        team2={m.team2}
                        players={players}
                        onPlayerClick={onPlayerClick}
                        light
                      />

                      {/* Score input — compact */}
                      <div
                        className="mt-3 pt-3"
                        style={{ borderTop: '1px solid rgba(15, 22, 17, 0.08)' }}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => {
                              const nv = clampScoreInput(String((parseInt(score1, 10) || 0) - 1));
                              setScore1(nv);
                              livePersist(nv, score2);
                            }}
                            aria-label="Ett poeng mindre til hjemmelaget"
                            className={PAD_MINUS_CLASS}
                            style={PAD_MINUS_STYLE}
                          >
                            −
                          </button>
                          <button
                            onClick={() => {
                              // A stepper cannot walk past the sane range
                              // (#372) — this pad is a kiosk on a wall, which
                              // is where a held-down + is least likely to be
                              // noticed.
                              const nv = clampScoreInput(String((parseInt(score1, 10) || 0) + 1));
                              setScore1(nv);
                              livePersist(nv, score2);
                            }}
                            aria-label="Ett poeng mer til hjemmelaget"
                            className="flex-shrink-0 flex items-center justify-center font-mono font-bold transition-all hover:brightness-95 active:scale-95"
                            style={{
                              width: 52,
                              height: 52,
                              borderRadius: 12,
                              background: '#c2f02f',
                              color: '#0f1611',
                              fontSize: 32,
                              border: '1px solid rgba(15, 22, 17, 0.08)',
                              boxShadow: '0 2px 8px -2px rgba(124, 168, 31, 0.3)',
                            }}
                          >
                            +
                          </button>
                          <input
                            type="number"
                            inputMode="numeric"
                            value={score1}
                            min={SCORE_MIN}
                            max={SCORE_MAX}
                            onChange={(e) => {
                              // `max` alone does not stop a typed 100 — it only
                              // marks the field invalid (#372).
                              const v = clampScoreInput(e.target.value);
                              setScore1(v);
                              livePersist(v, score2);
                            }}
                            placeholder="0"
                            className="text-center font-mono font-bold"
                            style={{
                              width: 76,
                              height: 52,
                              fontSize: 32,
                              background: '#ffffff',
                              color: '#0f1611',
                              borderRadius: 12,
                              border: '2px solid rgba(15, 22, 17, 0.15)',
                            }}
                            onKeyDown={(e) => e.key === 'Enter' && requestSubmit(m, score1, score2)}
                          />
                          <span
                            className="font-display text-2xl mx-0.5"
                            style={{ color: '#0f1611', opacity: 0.25 }}
                          >
                            –
                          </span>
                          <input
                            type="number"
                            inputMode="numeric"
                            value={score2}
                            min={SCORE_MIN}
                            max={SCORE_MAX}
                            onChange={(e) => {
                              const v = clampScoreInput(e.target.value);
                              setScore2(v);
                              livePersist(score1, v);
                            }}
                            placeholder="0"
                            className="text-center font-mono font-bold"
                            style={{
                              width: 76,
                              height: 52,
                              fontSize: 32,
                              background: '#ffffff',
                              color: '#0f1611',
                              borderRadius: 12,
                              border: '2px solid rgba(15, 22, 17, 0.15)',
                            }}
                            onKeyDown={(e) => e.key === 'Enter' && requestSubmit(m, score1, score2)}
                          />
                          <button
                            onClick={() => {
                              const nv = clampScoreInput(String((parseInt(score2, 10) || 0) + 1));
                              setScore2(nv);
                              livePersist(score1, nv);
                            }}
                            aria-label="Ett poeng mer til bortelaget"
                            className="flex-shrink-0 flex items-center justify-center font-mono font-bold transition-all hover:brightness-95 active:scale-95"
                            style={{
                              width: 52,
                              height: 52,
                              borderRadius: 12,
                              background: '#c2f02f',
                              color: '#0f1611',
                              fontSize: 32,
                              border: '1px solid rgba(15, 22, 17, 0.08)',
                              boxShadow: '0 2px 8px -2px rgba(124, 168, 31, 0.3)',
                            }}
                          >
                            +
                          </button>
                          <button
                            onClick={() => {
                              const nv = clampScoreInput(String((parseInt(score2, 10) || 0) - 1));
                              setScore2(nv);
                              livePersist(score1, nv);
                            }}
                            aria-label="Ett poeng mindre til bortelaget"
                            className={PAD_MINUS_CLASS}
                            style={PAD_MINUS_STYLE}
                          >
                            −
                          </button>
                        </div>
                        <div className="flex items-center justify-between mt-2.5">
                          <button
                            onClick={() => {
                              setScore1('');
                              setScore2('');
                              livePersist('', '');
                            }}
                            className="font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded transition-all hover:bg-bg-soft"
                            style={{
                              background: 'transparent',
                              color: '#6b766f',
                              border: '1px solid rgba(15, 22, 17, 0.10)',
                            }}
                          >
                            ↺ Nullstill
                          </button>
                          {(() => {
                            // THIS match's own phase — never a sibling's.
                            const phase =
                              submitStatus && String(submitStatus.matchId ?? '') === String(m.id)
                                ? submitStatus.phase
                                : 'idle';
                            const empty = !score1 && !score2;
                            const inert = empty || phase !== 'idle';
                            return (
                              <button
                                onClick={() => requestSubmit(m, score1, score2)}
                                disabled={inert}
                                className="px-5 py-2 rounded-lg font-mono text-xs font-bold uppercase tracking-wider transition-all hover:brightness-110 active:scale-95 flex items-center gap-1.5"
                                style={{
                                  background:
                                    phase === 'done' ? '#16a34a' : empty ? '#eef0ea' : '#0f1611',
                                  color:
                                    phase === 'done'
                                      ? '#f0fdf4'
                                      : phase === 'sending'
                                        ? '#6b766f'
                                        : empty
                                          ? '#94a098'
                                          : '#c2f02f',
                                  cursor: inert ? 'not-allowed' : 'pointer',
                                }}
                              >
                                <Check size={14} strokeWidth={3} />{' '}
                                {phase === 'done'
                                  ? 'Registrert'
                                  : phase === 'sending'
                                    ? 'Sender…'
                                    : 'Send resultat'}
                              </button>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              // FINISHED MATCH — compact, blue-grey, muted
              if (m.completed) {
                const isEditing = editingId === m.id;
                return (
                  <div
                    key={m.id}
                    className="px-5 py-2"
                    style={{
                      background:
                        'linear-gradient(180deg, rgba(71, 100, 130, 0.30), rgba(71, 100, 130, 0.12))',
                      borderBottom: '1px solid rgba(125, 155, 185, 0.10)',
                      opacity: isEditing ? 1 : 0.75,
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Check size={14} style={{ color: '#a3d957' }} />
                        <span
                          className="font-mono text-xs uppercase tracking-wider font-bold"
                          style={{ color: '#a3d957' }}
                        >
                          Kamp {matchNumber}
                        </span>
                        <span
                          className="font-mono text-[10px] uppercase tracking-wider"
                          style={{ color: '#94a098' }}
                        >
                          {m.phase === 'finals'
                            ? 'Finale'
                            : m.phase != null
                              ? `Fase ${m.phase}${m.round ? ` · R${m.round}` : ''}`
                              : m.round
                                ? `Runde ${m.round}`
                                : ''}
                          {m.time ? ` · ${m.time}` : ''}
                        </span>
                      </div>
                      <button
                        onClick={() => (isEditing ? setEditingId(null) : startEdit(m))}
                        className="font-mono text-[10px] uppercase tracking-wider px-3 py-1 rounded transition-all hover:brightness-125"
                        style={{ background: 'rgba(255, 255, 255, 0.08)', color: '#d4dad6' }}
                      >
                        {isEditing ? 'Avbryt' : 'Endre'}
                      </button>
                    </div>

                    {isEditing ? (
                      <div className="space-y-2">
                        <MatchupLines
                          team1={m.team1}
                          team2={m.team2}
                          players={players}
                          onPlayerClick={onPlayerClick}
                          size="sm"
                        />
                        <div className="flex items-center gap-3 justify-center">
                          <input
                            type="number"
                            inputMode="numeric"
                            value={editS1}
                            min={SCORE_MIN}
                            max={SCORE_MAX}
                            onChange={(e) => setEditS1(clampScoreInput(e.target.value))}
                            className="text-center font-mono text-xl font-bold py-1.5 rounded border-0"
                            style={{
                              width: 80,
                              background: 'rgba(255, 255, 255, 0.08)',
                              color: '#ffffff',
                            }}
                            placeholder="–"
                          />
                          <span className="font-mono text-sm opacity-40 text-white">–</span>
                          <input
                            type="number"
                            inputMode="numeric"
                            value={editS2}
                            min={SCORE_MIN}
                            max={SCORE_MAX}
                            onChange={(e) => setEditS2(clampScoreInput(e.target.value))}
                            className="text-center font-mono text-xl font-bold py-1.5 rounded border-0"
                            style={{
                              width: 80,
                              background: 'rgba(255, 255, 255, 0.08)',
                              color: '#ffffff',
                            }}
                            placeholder="–"
                          />
                          <button
                            onClick={() => saveEdit(m.id)}
                            className="px-4 py-1.5 rounded font-mono text-xs font-bold uppercase tracking-wider transition-all hover:brightness-110"
                            style={{ background: '#c2f02f', color: '#0f1611' }}
                          >
                            Oppdater
                          </button>
                        </div>
                      </div>
                    ) : (
                      <MatchupLines
                        team1={m.team1}
                        team2={m.team2}
                        players={players}
                        onPlayerClick={onPlayerClick}
                        size="sm"
                        score={{
                          s1: m.score1,
                          s2: m.score2,
                          eloChange1: m.eloChange1,
                          eloChange2: m.eloChange2,
                          teamElo1Pre: m.teamElo1Pre,
                          teamElo2Pre: m.teamElo2Pre,
                          t1NewbieMul: m.t1NewbieMul,
                          t2NewbieMul: m.t2NewbieMul,
                        }}
                      />
                    )}
                  </div>
                );
              }

              // FUTURE MATCH — compact, amber, muted
              return (
                <div
                  key={m.id}
                  className="px-5 py-2"
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(180, 130, 60, 0.12), rgba(180, 130, 60, 0.30))',
                    borderBottom: '1px solid rgba(210, 160, 90, 0.10)',
                    opacity: 0.7,
                  }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Timer size={14} style={{ color: '#d4a84a' }} />
                    <span
                      className="font-mono text-xs uppercase tracking-wider font-bold"
                      style={{ color: '#d4a84a' }}
                    >
                      Kamp {matchNumber}
                    </span>
                    <span
                      className="font-mono text-[10px] uppercase tracking-wider"
                      style={{ color: '#94a098' }}
                    >
                      {m.phase === 'finals'
                        ? 'Finale'
                        : m.phase != null
                          ? `Fase ${m.phase}${m.round ? ` · R${m.round}` : ''}`
                          : m.round
                            ? `Runde ${m.round}`
                            : ''}
                      {m.time ? ` · ${m.time}` : ''}
                    </span>
                  </div>
                  <MatchupLines
                    team1={m.team1}
                    team2={m.team2}
                    players={players}
                    onPlayerClick={onPlayerClick}
                    size="sm"
                  />
                </div>
              );
            })}

            {/* Bunnmessage: scroll down for newer */}
            <div className="px-5 py-2 text-center" style={{ color: '#4a544c' }}>
              <span className="font-mono text-[10px] uppercase tracking-wider">
                ↓ kommende kamper
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation modal */}
      {pendingConfirm && (
        <ConfirmResultModal
          payload={pendingConfirm}
          players={players}
          onConfirm={confirmSubmit}
          onCancel={() => setPendingConfirm(null)}
        />
      )}
    </div>
  );
}
